import { parsePageResult } from '@zoryn/components/grid'
import type { FilterPayloadNode } from '@zoryn/components/grid'
import type { CommonResult } from '@zoryn/network'
import type { HttpRequest } from '@zoryn/network'
import type { RuleContentType, RuleRecord } from './rules'

export const RULES_MODULE = '/system/rules'

export function resolveRulesUrls(module = RULES_MODULE) {
  return {
    queryByExamplePage: `${module}/queryByExamplePage`,
    insertRow: `${module}/insertRow`,
    updateById: `${module}/updateById`,
    deleteById: `${module}/deleteById`,
    batchDelete: `${module}/batchDelete`,
  }
}

export function isClientGeneratedRuleId(id: unknown) {
  return typeof id === 'string' && /^(rule|r|cond|group)_\d+$/.test(id)
}

export function isPersistedRule(record: Pick<RuleRecord, 'id'>) {
  return Boolean(record.id) && !isClientGeneratedRuleId(record.id)
}

function parseJsonField<T>(value: unknown): T | null {
  if (value == null) {
    return null
  }
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T
    } catch {
      return null
    }
  }
  return value as T
}

function serializeJsonField(value: unknown): string | null {
  if (value == null) {
    return null
  }
  if (typeof value === 'string') {
    return value
  }
  try {
    return JSON.stringify(value)
  } catch {
    return null
  }
}

export function normalizeRuleRecord(row: Record<string, unknown>): RuleRecord {
  const enabled =
    row.enabled != null
      ? row.enabled !== false
      : row.status != null
        ? row.status !== false
        : true

  return {
    id: String(row.id ?? ''),
    system: String(row.system ?? row.systemFlag ?? ''),
    module: row.module != null ? String(row.module) : undefined,
    ruleName: String(row.ruleName ?? ''),
    ruleDescription: String(row.ruleDescription ?? row.ruleDesc ?? ''),
    rulesJson: parseJsonField(row.rulesJson ?? row.ruleJson),
    resultJson: (parseJsonField<Record<string, unknown>>(
      row.resultJson ?? row.ruleResult
    ) ?? {}) as RuleRecord['resultJson'],
    content: row.content as RuleContentType | undefined,
    ruleScript: String(row.ruleScript ?? ''),
    ruleExpr: String(row.ruleExpr ?? ''),
    scriptLanguage: 'aviator',
    enabled,
    priority: row.priority != null ? Number(row.priority) : undefined,
  }
}

export function buildRulesQueryFilters(system: string, module?: string): FilterPayloadNode[] {
  const filters: FilterPayloadNode[] = [
    { operator: 'eq', field: 'systemFlag', value: system },
  ]
  if (module) {
    filters.push({ operator: 'eq', field: 'module', value: module })
  }
  return filters
}

export function prepareRuleForSave(
  record: RuleRecord,
  options?: { forInsert?: boolean }
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    systemFlag: record.system,
    module: record.module,
    ruleName: record.ruleName,
    ruleDesc: record.ruleDescription,
    ruleJson: serializeJsonField(record.rulesJson),
    ruleResult: serializeJsonField(record.resultJson),
    ruleScript: record.ruleScript,
    status: record.enabled !== false,
  }

  if (!options?.forInsert && isPersistedRule(record)) {
    payload.id = record.id
  }

  return payload
}

export async function queryRulesList(
  request: HttpRequest,
  options: { system: string; module?: string }
): Promise<RuleRecord[]> {
  const urls = resolveRulesUrls()
  const res = await request.get({
    url: urls.queryByExamplePage,
    params: {
      currentPage: 1,
      pageSize: 9999,
      pageFlag: false,
      filters: JSON.stringify(buildRulesQueryFilters(options.system, options.module)),
    },
  })
  const { list } = parsePageResult<Record<string, unknown>>(res as never)
  return list.map((item) => normalizeRuleRecord(item))
}

function extractSavedRule(
  response: unknown,
  fallback: RuleRecord
): RuleRecord {
  if (response != null && typeof response === 'object') {
    const body = response as CommonResult<Record<string, unknown>>
    if (body.data != null && typeof body.data === 'object') {
      return normalizeRuleRecord(body.data)
    }
    if ('id' in body || 'ruleName' in body) {
      return normalizeRuleRecord(body as Record<string, unknown>)
    }
  }
  return fallback
}

export async function insertRule(
  request: HttpRequest,
  record: RuleRecord
): Promise<RuleRecord> {
  const urls = resolveRulesUrls()
  const res = await request.post({
    url: urls.insertRow,
    params: prepareRuleForSave(record, { forInsert: true }),
  })
  return extractSavedRule(res, record)
}

export async function updateRule(
  request: HttpRequest,
  record: RuleRecord
): Promise<RuleRecord> {
  const urls = resolveRulesUrls()
  const res = await request.post({
    url: urls.updateById,
    params: prepareRuleForSave(record),
  })
  return extractSavedRule(res, record)
}

export async function deleteRules(
  request: HttpRequest,
  records: RuleRecord[]
): Promise<void> {
  const persisted = records.filter((item) => isPersistedRule(item))
  if (!persisted.length) {
    return
  }
  const urls = resolveRulesUrls()
  if (persisted.length === 1) {
    await request.post({
      url: urls.deleteById,
      params: { id: persisted[0].id },
    })
    return
  }
  await request.post({
    url: urls.batchDelete,
    params: persisted,
  })
}
